import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Divider,
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import HistoryIcon from '@mui/icons-material/History';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { useTranslation } from 'react-i18next';
import { useAssistantStore, type AssistantView } from '@/stores/assistantStore';
import { useUIStore } from '@/stores/uiStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAssistantRealtime } from '@/hooks/useAssistantRealtime';
import { useInsightRealtime } from '@/hooks/useInsightRealtime';
import { useInsights, isActiveInsight } from '@/hooks/useInsights';
import {
  useAssistantChat,
  type ConversationSummary,
} from '@/hooks/useAssistantChat';
import { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from '@/components/layout/Sidebar';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { InsightsInbox } from './InsightsInbox';
import { ASSISTANT_PANEL_WIDTH, useAssistantSideBySide } from './assistantLayout';
import {
  GUIDE_NOTICE,
  guideProgress,
  translateGuideKey,
  useGuideStore,
  type GuideTranslator,
} from '@/lib/guides/engine';

function asGuideTranslator(translate: unknown): GuideTranslator {
  const call = translate as (key: string, options?: object) => unknown;
  return (key, options) => call(key, options);
}

export function AssistantPanel() {
  const { t } = useTranslation('assistant');
  const { t: tg } = useTranslation('guides', { useSuspense: false });
  const { t: tc } = useTranslation('common');
  const guideRun = useGuideStore((state) => state.active);
  const stopGuide = useGuideStore((state) => state.cancel);
  const isMobile = useIsMobile();
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const canShowSideBySide = useAssistantSideBySide(sidebarWidth);
  const isOpen = useAssistantStore((s) => s.isOpen);
  const setOpen = useAssistantStore((s) => s.setOpen);
  const view = useAssistantStore((s) => s.view);
  const setView = useAssistantStore((s) => s.setView);
  const conversationId = useAssistantStore((s) => s.conversationId);
  const messages = useAssistantStore((s) => s.messages);

  useAssistantRealtime();
  useInsightRealtime();

  // Drives the inbox tab's active-count badge; the inbox reuses this cached query.
  const { data: insightsData } = useInsights({}, { enabled: isOpen });
  const activeInsightCount = (insightsData?.insights ?? []).filter((i) => isActiveInsight(i)).length;

  const { startNewConversation, listConversations, loadHistory } = useAssistantChat();
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const [history, setHistory] = useState<ConversationSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Refresh the conversation list whenever the panel opens or the active
  // conversation changes — keeps the picker in sync with what the user has
  // started in the current session.
  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const items = await listConversations();
      setHistory(items);
    } finally {
      setHistoryLoading(false);
    }
  }, [listConversations]);

  useEffect(() => {
    if (isOpen) {
      void refreshHistory();
    }
  }, [isOpen, conversationId, refreshHistory]);

  const handleOpenHistory = (event: React.MouseEvent<HTMLElement>): void => {
    setHistoryAnchor(event.currentTarget);
    void refreshHistory();
  };

  const handleCloseHistory = (): void => {
    setHistoryAnchor(null);
  };

  const handleSelectConversation = (id: string): void => {
    setHistoryAnchor(null);
    if (id !== conversationId) {
      void loadHistory(id);
    }
  };

  const handleNewConversation = (): void => {
    startNewConversation();
  };

  const showNewConversationButton = messages.length > 0 || conversationId !== null;
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  const panelBody = (
    <>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          minHeight: 56,
        }}
      >
        <SmartToyOutlinedIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
          {t('title')}
        </Typography>

        <Tooltip title={t('previousConversations')}>
          <IconButton
            size="small"
            onClick={handleOpenHistory}
            aria-label={t('previousConversations')}
          >
            <HistoryIcon />
          </IconButton>
        </Tooltip>

        {showNewConversationButton && (
          <Button
            size="small"
            color="inherit"
            startIcon={<AddCommentOutlinedIcon />}
            onClick={handleNewConversation}
            sx={{ mr: 1 }}
          >
            {t('newConversation')}
          </Button>
        )}

        <Tooltip title={tc('close')}>
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            aria-label={tc('close')}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {guideRun && (
        <Box
          data-testid="guide-header"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pb: 1 }}
        >
          <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
            {translateGuideKey(asGuideTranslator(tg), guideRun.guide.titleKey) ??
              translateGuideKey(asGuideTranslator(t), GUIDE_NOTICE.missingText) ??
              ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('guide.progress', guideProgress(guideRun))}
          </Typography>
          <Button size="small" color="inherit" onClick={stopGuide}>
            {t('guide.stop')}
          </Button>
        </Box>
      )}

      <Menu
        anchorEl={historyAnchor}
        open={Boolean(historyAnchor)}
        onClose={handleCloseHistory}
        slotProps={{ paper: { sx: { minWidth: 280 } } }}
      >
        {historyLoading && history.length === 0 ? (
          <MenuItem disabled>{tc('loading')}</MenuItem>
        ) : history.length === 0 ? (
          <MenuItem disabled>{t('noPreviousConversations')}</MenuItem>
        ) : (
          history.map((c) => {
            const stamp = c.lastMessageAt ?? c.createdAt;
            return (
              <MenuItem
                key={c.id}
                selected={c.id === conversationId}
                onClick={() => handleSelectConversation(c.id)}
              >
                <ListItemText
                  primary={dateFormatter.format(new Date(stamp))}
                  secondary={c.id === conversationId ? t('currentConversation') : null}
                />
              </MenuItem>
            );
          })
        )}
      </Menu>

      <Divider />

      {/* View switch: chat vs proactive insights inbox */}
      <Tabs
        value={view}
        onChange={(_, next: AssistantView) => setView(next)}
        variant="fullWidth"
        sx={{ minHeight: 42, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="chat" label={t('tabs.chat')} sx={{ minHeight: 42 }} />
        <Tab
          value="insights"
          label={
            activeInsightCount > 0
              ? `${t('tabs.insights')} (${String(activeInsightCount)})`
              : t('tabs.insights')
          }
          sx={{ minHeight: 42 }}
        />
      </Tabs>

      {view === 'chat' ? (
        <>
          {/* Messages */}
          <ChatMessageList />
          {/* Input */}
          <ChatInput />
        </>
      ) : (
        <InsightsInbox />
      )}
    </>
  );

  // Enough real room beside the sidebar and a usable content column: render
  // as a non-modal panel (no backdrop, no focus trap) that reserves its own
  // flex space, exactly like the left Sidebar does. Otherwise (mobile, or a
  // desktop window too narrow to show both usably) fall back to the
  // existing temporary/overlay behavior.
  if (isOpen && canShowSideBySide) {
    return (
      <Drawer
        anchor="right"
        variant="permanent"
        sx={{ width: ASSISTANT_PANEL_WIDTH, flexShrink: 0 }}
        PaperProps={{
          sx: {
            width: ASSISTANT_PANEL_WIDTH,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {panelBody}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={() => setOpen(false)}
      variant="temporary"
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : ASSISTANT_PANEL_WIDTH,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {panelBody}
    </Drawer>
  );
}
